import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { usePageContext } from 'vike-react/usePageContext';
import { ShareNetwork, FileText, MapPin, CheckCircle, Buildings } from '@phosphor-icons/react';
import { Icon } from '../../design-system/icons/Icon';
import { Link } from '../Link';
import { fixMojibake } from '../../utils/text/fix-mojibake';
import { trackEvent, trackJobClick } from '../../utils/analytics/analytics';
import { MatchDonutWithLoginPrompt } from '../MatchDonut';
import { useHitShared } from './hooks/useHitShared';
import { useApplicationKitActions } from '../../hooks/useApplicationKitActions';
import { getCurrentToolRoute } from '../../utils/routing/route-utils';
import { ApplicationStateMenu } from '../application-state';
import { CompanyLogo } from '../CompanyLogo';
import { CompanyName } from '../CompanyName';
import { CompanyFollowButton } from '../company/CompanyFollowButton';
import { getSinglePrioritizedLocation } from '../location/utils/locationUtils';
import { adjustBrandColor } from '../../utils/brand-color/adjust-brand-color';
import type { HitProps } from './types';
import { calculateFreshness } from '../dates/utils/freshnessCalculation';
import { formatSrpSalaryParts } from '../../utils/data/format-job-card-labels';
import { formatOriginalSalaryParts } from '../../utils/data/format-original-salary-parts';
import { useSalaryDisplayPreference } from '../../contexts/SalaryDisplayPreferenceContext';
import { SalaryInferenceExplainer } from '../SalaryInferenceExplainer';
import { resolveSalaryInferenceExplanationKind } from '../../utils/job-detail/resolve-salary-inference-explanation-kind';
import { formatSalaryInferenceExplanation } from '../../utils/job-detail/salary-inference-explanation-intl';
import { JD_MORE_INFO_CUSTOM_TRIGGER_BARE } from '../../utils/job-detail/job-detail-more-info-affordance';
import { shouldLinkCompanyProfile } from '../../utils/company/should-link-company-profile';
import { companyProfileUrlPath } from '../../utils/routing/company-profile-url-path';
import { resolveSearchHitIsTyomarkkinatori } from '../../utils/search/resolve-search-hit-is-tyomarkkinatori';
import {
  resolveSearchHitCompanyId,
  resolveSearchHitHostedLogoUrl,
} from '../../utils/search/resolve-search-hit-company-display';
import { normalizeToStringArray } from '../../utils/data/normalize-to-string-array';
import { PipelineStageChip } from './components/PipelineStageChip';
import { resolvePipelineStageChip } from '../../utils/job-tracking/resolve-pipeline-stage-chip';
import { useFormatMessage } from '../../i18n-review/client/Msg';

/** Pipeline stages that get kit-awareness but never a creation CTA — the application already went out. */
const PIPELINE_TOOL_ROUTES = new Set([
  '/tools/applied-jobs',
  '/tools/interviewing-jobs',
  '/tools/offer-jobs',
  '/tools/rejected-jobs',
  '/tools/hired-jobs',
]);

/**
 * "Brand color panel" redesign of the SRP job card. Reuses useHitShared for all
 * state/actions (bookmark, hide/apply-state menu, match score, click-to-filter meta links);
 * only the presentation layer differs from HitCard. See utils/brand-color/adjust-brand-color.ts
 * for the panel-fill algorithm and hit-card-v2.css for styles. Read-only contexts (server-
 * rendered SRP hits before hydration, the company profile page) render the equivalent markup
 * via HitCompactV2View.tsx directly.
 */
export const HitCompactV2: React.FC<HitProps> = memo(({ hit, sendEvent, index }) => {
  const intl = useIntl();
  const formatMessage = useFormatMessage();
  const shared = useHitShared({ hit, sendEvent });
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const [isNavigatingToDetails, setIsNavigatingToDetails] = useState(false);
  const pageContext = usePageContext();
  const pathname = (pageContext as { urlPathname?: string })?.urlPathname ?? '';
  const currentToolRoute = getCurrentToolRoute(pathname);
  const isSavedJobsPage = currentToolRoute === '/tools/saved-jobs';
  const isPipelineToolPage = currentToolRoute !== null && PIPELINE_TOOL_ROUTES.has(currentToolRoute);
  const { getApplicationKit } = useApplicationKitActions();
  const applicationKit = isSavedJobsPage || isPipelineToolPage ? getApplicationKit(hit.objectID) : null;
  // Saved jobs always offers the tailor CTA (create or reopen); pipeline pages only show it once a
  // kit exists — the application already went out, so there's nothing to create from here.
  const showKitAction = isSavedJobsPage || (isPipelineToolPage && Boolean(applicationKit));

  const {
    jobUrl,
    articleId,
    titleId,
    jobTitle,
    isSaved,
    isApplied,
    isHidden,
    currentApplicationState,
    appliedAt,
    brandColour,
    matchScore,
    matchScoreData,
    matchScoreMeta,
    isAuthenticated,
    onLoginClick,
    actions,
    getUrlForIndexUiState,
    pushIndexUiState,
    makeRefinementState,
    browsedJobRef,
    menuOpenSource,
    setMenuOpenSource,
    buttonState,
  } = shared;

  const adjustedBrand = useMemo(() => adjustBrandColor(brandColour), [brandColour]);

  const stageChip = useMemo(
    () => resolvePipelineStageChip({ isSaved, isApplied, applicationState: currentApplicationState }),
    [isSaved, isApplied, currentApplicationState]
  );

  const requiredLanguageTags = useMemo(() => normalizeToStringArray(hit.requiredLanguages), [hit.requiredLanguages]);

  const hitId = hit.objectID || (hit as unknown as { id?: string }).id;
  useEffect(() => {
    if (sendEvent) {
      sendEvent('view', hit, 'Job Viewed (CA English)');
    }
  }, [sendEvent, hitId]);

  const singleLocation = useMemo(() => getSinglePrioritizedLocation(hit, undefined), [hit]);

  const salaryInferenceKind = useMemo(
    () =>
      resolveSalaryInferenceExplanationKind({
        salaryIsEstimate: hit.salaryIsEstimate,
        salaryRangeMin: hit.salaryRangeMin,
        salaryRangeMax: hit.salaryRangeMax,
        salaryDetails: hit.salaryDetails,
      }),
    [hit.salaryIsEstimate, hit.salaryRangeMin, hit.salaryRangeMax, hit.salaryDetails],
  );
  const salaryInferenceText = useMemo(
    () => formatSalaryInferenceExplanation(salaryInferenceKind, intl.formatMessage),
    [salaryInferenceKind, intl],
  );
  const salaryParts = useMemo(
    () => formatSrpSalaryParts({ salaryRangeMin: hit.salaryRangeMin, salaryRangeMax: hit.salaryRangeMax, locale: intl.locale }),
    [hit.salaryRangeMin, hit.salaryRangeMax, intl.locale],
  );
  const salaryNotSpecifiedLabel = formatMessage({ id: 'hit.salaryNotSpecified.card', defaultMessage: 'Salary not specified', description: 'Shown when salary is not specified in job card' });
  const hasSalary = salaryParts.length > 0;
  const isEstimate = Boolean(hit.salaryIsEstimate);
  /** A quote pulled straight from the listing text — no inference needed to explain it (see resolveSalaryInferenceExplanationKind). */
  const isVerifiedSalary = hasSalary && salaryInferenceKind === null;

  const { mode: salaryDisplayMode } = useSalaryDisplayPreference();
  const originalSalaryParts = useMemo(
    () =>
      formatOriginalSalaryParts({
        min: hit.originalSalaryRangeMin,
        max: hit.originalSalaryRangeMax,
        currency: hit.originalCurrency,
        locale: intl.locale,
      }),
    [hit.originalSalaryRangeMin, hit.originalSalaryRangeMax, hit.originalCurrency, intl.locale],
  );
  const hasOriginalSalary = originalSalaryParts.length > 0;
  const showOriginalSalary = salaryDisplayMode === 'original' && hasOriginalSalary;
  const displaySalaryParts = showOriginalSalary ? originalSalaryParts : salaryParts;
  const originalPeriodLabel = useMemo(() => {
    switch (hit.originalSalaryPeriod) {
      case 'annual':
        return formatMessage({ id: 'salary.period.year', defaultMessage: '/yr', description: 'Short abbreviation shown after a salary figure for an annual (per-year) pay period' });
      case 'monthly':
        return formatMessage({ id: 'salary.period.month', defaultMessage: '/mo', description: 'Short abbreviation shown after a salary figure for a monthly pay period' });
      case 'daily':
        return formatMessage({ id: 'salary.period.day', defaultMessage: '/day', description: 'Short abbreviation shown after a salary figure for a daily pay period' });
      case 'hourly':
        return formatMessage({ id: 'salary.period.hour', defaultMessage: '/hr', description: 'Short abbreviation shown after a salary figure for an hourly pay period' });
      default:
        return '';
    }
  }, [hit.originalSalaryPeriod, intl]);

  const freshnessLabel = hit._lastSeenLabel
    ? hit._lastSeenLabel
    : (() => {
        const freshness = calculateFreshness(hit.lastSeen as string | number | undefined);
        if (!freshness) return null;
        return formatMessage(
          {
            id: freshness.id,
            defaultMessage:
              freshness.id === 'hit.lastSeen.minutesAgo'
                ? '{value, plural, one {{value} minute ago} other {{value} minutes ago}}'
                : freshness.id === 'hit.lastSeen.hoursAgo'
                  ? '{value, plural, one {{value} hour ago} other {{value} hours ago}}'
                  : '{value, plural, one {{value} day ago} other {{value} days ago}}',
            description: 'Indicates how recently the job was last seen/crawled.',
          },
          { value: freshness.value },
        );
      })();

  const isTyomarkkinatori = useMemo(
    () => resolveSearchHitIsTyomarkkinatori(hit as unknown as Record<string, unknown>),
    [hit.isTyomarkkinatori, hit.url],
  );
  const companyLogoKey = useMemo(
    () => resolveSearchHitCompanyId(hit as unknown as Record<string, unknown>),
    [hit.companyId, hit.companyName],
  );
  const hostedLogoUrl = useMemo(
    () => resolveSearchHitHostedLogoUrl(hit as unknown as Record<string, unknown>),
    [hit.logoUrl, hit.isTyomarkkinatori, hit.url],
  );
  const companyDisplayName = hit.companyName ?? companyLogoKey;
  const canLinkCompanyProfile = shouldLinkCompanyProfile({ companyId: companyLogoKey, isTyomarkkinatori });
  const companyProfileHref = useMemo(
    () => (canLinkCompanyProfile && companyLogoKey ? companyProfileUrlPath(intl.locale as 'en' | 'fi' | 'sv', companyLogoKey) : ''),
    [canLinkCompanyProfile, companyLogoKey, intl.locale],
  );
  const companyLinkTitle = formatMessage(
    { id: 'hit.companyLinkTitle', defaultMessage: 'Open company profile — data and insights for {company}', description: 'Tooltip for the employer logo and name link on a job card' },
    { company: companyDisplayName },
  );

  const handleJobDetailsClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    const isPrimaryClick = e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;
    if (isPrimaryClick) setIsNavigatingToDetails(true);
    if (sendEvent) {
      sendEvent('click', hit, 'Job Details Clicked');
      sendEvent('conversion', hit, 'Job Details Clicked');
    }
    trackJobClick({
      jobId: hit.objectID,
      jobTitle: hit.normalisedTitle || hit.title || '',
      companyId: hit.companyId || '',
      locale: intl.locale,
    });
  };

  const metaFields: { key: string; label: string; facet?: string; value?: string }[] = [];
  if (hit.remoteness?.trim()) metaFields.push({ key: 'remote', label: fixMojibake(hit.remoteness), facet: 'remoteness', value: hit.remoteness });
  if (hit.jobType?.trim()) metaFields.push({ key: 'jobType', label: fixMojibake(hit.jobType), facet: 'jobType', value: hit.jobType });
  if (hit.normalisedRoleLevel?.trim()) metaFields.push({ key: 'roleLevel', label: fixMojibake(hit.normalisedRoleLevel), facet: 'normalisedRoleLevel', value: hit.normalisedRoleLevel });
  if (requiredLanguageTags.length > 0) metaFields.push({ key: 'language', label: requiredLanguageTags.map(fixMojibake).join(', '), facet: 'requiredLanguages', value: requiredLanguageTags[0] });

  return (
    <article
      id={articleId}
      ref={browsedJobRef}
      className={`hit-card-v2 ${isNavigatingToDetails ? 'hit-card-v2--navigating' : ''}`}
      title={jobTitle}
      aria-labelledby={titleId}
    >
      <div className="hit-card-v2__panel" style={{ background: adjustedBrand.panel, color: adjustedBrand.on }}>
        {companyProfileHref && canLinkCompanyProfile ? (
          <Link href={companyProfileHref} locale={intl.locale} className="hit-card-v2__logo-tile" title={companyLinkTitle}>
            <CompanyLogo
              companyName={companyLogoKey || ''}
              initialsSourceName={companyDisplayName ?? companyLogoKey}
              registryCompanyFormDescription={hit.registryCompanyFormDescription}
              isTyomarkkinatori={isTyomarkkinatori}
              hostedLogoUrl={hostedLogoUrl}
              size={96}
              loading={(index ?? 0) < 3 ? 'eager' : 'lazy'}
            />
          </Link>
        ) : (
          <div className="hit-card-v2__logo-tile">
            <CompanyLogo
              companyName={companyLogoKey || ''}
              initialsSourceName={companyDisplayName ?? companyLogoKey}
              registryCompanyFormDescription={hit.registryCompanyFormDescription}
              isTyomarkkinatori={isTyomarkkinatori}
              hostedLogoUrl={hostedLogoUrl}
              size={96}
              loading={(index ?? 0) < 3 ? 'eager' : 'lazy'}
            />
          </div>
        )}
        {singleLocation.display ? (
          <div className="hit-card-v2__panel-location">
            <MapPin weight="fill" className="hit-card-v2__panel-location-icon" />
            {singleLocation.locationForUrl ? (
              <Link
                href={getUrlForIndexUiState(makeRefinementState('location', singleLocation.locationForUrl))}
                className="hit-card-v2__panel-location-text"
                onClick={(e) => {
                  e.preventDefault();
                  pushIndexUiState(makeRefinementState('location', singleLocation.locationForUrl));
                }}
              >
                {fixMojibake(singleLocation.display)}
              </Link>
            ) : (
              <span className="hit-card-v2__panel-location-text">{fixMojibake(singleLocation.display)}</span>
            )}
          </div>
        ) : null}
        {companyLogoKey || freshnessLabel ? (
          <div className="hit-card-v2__panel-footer">
            {companyLogoKey ? (
              <div className="hit-card-v2__panel-follow">
                <CompanyFollowButton compact onBrand surface="srp-hit" companyId={companyLogoKey} companyName={companyDisplayName ?? companyLogoKey} />
              </div>
            ) : null}
            {freshnessLabel ? <div className="hit-card-v2__posted">{freshnessLabel}</div> : null}
          </div>
        ) : null}
      </div>

      <div className="hit-card-v2__content">
        <div className="hit-card-v2__top-row">
          <div className="hit-card-v2__company-block">
            {companyProfileHref && canLinkCompanyProfile ? (
              <Link href={companyProfileHref} locale={intl.locale} className="hit-card-v2__company-name" title={companyLinkTitle}>
                <span className="hit-card-v2__company-name-text">
                  <CompanyName name={companyDisplayName ?? companyLogoKey} />
                </span>
                <Buildings weight="fill" className="hit-card-v2__company-name-icon" />
              </Link>
            ) : (
              <span className="hit-card-v2__company-name">
                <span className="hit-card-v2__company-name-text">
                  <CompanyName name={companyDisplayName ?? companyLogoKey} />
                </span>
              </span>
            )}
          </div>
          <div className="hit-card-v2__actions">
            <MatchDonutWithLoginPrompt
              score={matchScore}
              size={26}
              onLoginClick={onLoginClick}
              breakdown={matchScoreData ?? undefined}
              isAuthenticated={isAuthenticated}
              matchScoreMeta={matchScoreMeta}
              ringColorOverride={adjustedBrand.panel}
              labelColorOverride={{ light: '#16150F', dark: '#F3EFE4' }}
            />
            <button
              type="button"
              className={`hit-card-v2__icon-btn ${isSaved ? 'hit-card-v2__icon-btn--saved' : ''}`}
              aria-label={
                isSaved
                  ? formatMessage({ id: 'hit.saved', defaultMessage: 'Saved', description: 'Button label when job is saved' })
                  : formatMessage({ id: 'hit.save', defaultMessage: 'Save', description: 'Button label to save a job' })
              }
              onClick={actions.handleToggleSaved}
            >
              <Icon name="bookmark" variant={isSaved ? 'solid' : 'outline'} useMaskForColor className="hit-card-v2__icon" />
            </button>
            <button
              type="button"
              className="hit-card-v2__icon-btn"
              aria-label={formatMessage({ id: 'hit.share.detail', defaultMessage: 'Share', description: 'Button label to share a job on detail page' })}
              onClick={() => void actions.handleShare()}
            >
              <ShareNetwork className="hit-card-v2__icon h-3.5 w-3.5" />
            </button>
            <button
              ref={menuButtonRef}
              type="button"
              className="hit-card-v2__icon-btn"
              aria-label={buttonState ? buttonState.label : formatMessage({ id: 'hit.moreOptions', defaultMessage: 'More options' })}
              onClick={() => setMenuOpenSource(menuOpenSource ? null : 'desktop')}
            >
              <Icon name="ellipsisVertical" useMaskForColor className="hit-card-v2__icon" />
            </button>
            {showKitAction && (
              <Link
                href={`${jobUrl}?tailor=cv`}
                className={`hit-card-v2__icon-btn ${applicationKit ? 'hit-card-v2__icon-btn--saved' : ''}`}
                aria-label={
                  applicationKit
                    ? formatMessage({
                        id: 'tools.applicationKits.kitReadyBadge',
                        defaultMessage: 'Application kit ready',
                        description: 'Badge/label shown on a job row when a tailored CV or cover letter already exists for it',
                      })
                    : formatMessage({
                        id: 'tools.savedJobs.tailorCvAction',
                        defaultMessage: 'Tailor CV',
                        description: 'Action to tailor a CV for this job from the saved jobs list',
                      })
                }
                onClick={() =>
                  trackEvent(
                    applicationKit
                      ? { name: 'application_kit_interaction', params: { action: 'reopen', job_id: hit.objectID, locale: intl.locale } }
                      : {
                          name: 'tailor_cv_interaction',
                          params: { action: 'open', job_id: hit.objectID, company_id: hit.companyId, locale: intl.locale, source: 'saved_jobs' },
                        }
                  )
                }
              >
                <FileText weight={applicationKit ? 'fill' : 'regular'} className="hit-card-v2__icon h-3.5 w-3.5" />
              </Link>
            )}
          </div>
        </div>

        <div className="hit-card-v2__title-block">
          <h3 id={titleId} className="sr-only">{jobTitle}</h3>
          <Link
            href={jobUrl}
            className="hit-card-v2__title"
            onClick={handleJobDetailsClick}
          >
            {fixMojibake(hit.normalisedTitle ?? '')}
          </Link>
          {hit.title && hit.title !== hit.normalisedTitle ? (
            <div className="hit-card-v2__subtitle">{fixMojibake(hit.title)}</div>
          ) : null}
          {stageChip ? <PipelineStageChip stage={stageChip} appliedAt={appliedAt} /> : null}
        </div>

        <div className="hit-card-v2__salary-row">
          {hasSalary ? (
            <SalaryInferenceExplainer explanationText={salaryInferenceText} triggerClassName={JD_MORE_INFO_CUSTOM_TRIGGER_BARE}>
              <span className={`hit-card-v2__salary-chip ${isVerifiedSalary ? 'hit-card-v2__salary-chip--verified' : ''}`}>
                {isVerifiedSalary ? <CheckCircle weight="fill" className="hit-card-v2__salary-chip-icon" /> : null}
                {displaySalaryParts.map((amt, i) => (
                  <span className="hit-card-v2__salary-chip-amt" key={i}>
                    {i > 0 ? `- ${amt}` : amt}
                  </span>
                ))}
                <span className="hit-card-v2__salary-chip-unit">
                  {showOriginalSalary
                    ? originalPeriodLabel
                    : isEstimate
                      ? formatMessage({ id: 'hitV2.estMonthly', defaultMessage: 'Est. Monthly', description: 'Salary period label on the redesigned job card panel, estimated figure' })
                      : formatMessage({ id: 'hitV2.monthly', defaultMessage: 'Monthly', description: 'Salary period label on the redesigned job card panel' })}
                  {salaryInferenceText.trim() ? (
                    <span className="hit-card-v2__salary-info-dot" aria-hidden="true">i</span>
                  ) : null}
                </span>
              </span>
            </SalaryInferenceExplainer>
          ) : (
            <span className="hit-card-v2__salary-not-specified">{salaryNotSpecifiedLabel}</span>
          )}
        </div>

        {metaFields.length > 0 ? (
          <div className="hit-card-v2__meta">
            {metaFields.map((field, i) => (
              <React.Fragment key={field.key}>
                {i > 0 ? <span aria-hidden className="hit-card-v2__meta-sep">·</span> : null}
                {field.facet && field.value ? (
                  <Link
                    href={getUrlForIndexUiState(makeRefinementState(field.facet, field.value))}
                    className="hit-card-v2__meta-item hit-card-v2__meta-item--link"
                    onClick={(e) => {
                      e.preventDefault();
                      pushIndexUiState(makeRefinementState(field.facet as string, field.value as string));
                    }}
                  >
                    {field.label}
                  </Link>
                ) : (
                  <span className="hit-card-v2__meta-item">{field.label}</span>
                )}
              </React.Fragment>
            ))}
          </div>
        ) : null}

        <Link
          href={jobUrl}
          className={`hit-card-v2__cta ${isNavigatingToDetails ? 'hit-card-v2__cta--loading' : ''}`}
          onClick={handleJobDetailsClick}
        >
          {formatMessage({ id: 'hitV2.viewJob', defaultMessage: 'View job', description: 'Primary CTA button on the redesigned job card' })}
        </Link>
      </div>

      {menuOpenSource && (
        <ApplicationStateMenu
          currentApplicationState={currentApplicationState}
          isApplied={isApplied}
          isHidden={isHidden}
          onSetApplicationState={actions.handleSetApplicationState}
          onToggleApplied={actions.handleToggleApplied}
          onToggleHide={actions.handleToggleHide}
          onClose={() => setMenuOpenSource(null)}
          className="w-56"
          triggerRef={menuButtonRef as unknown as React.RefObject<HTMLElement>}
          usePortal={true}
        />
      )}
    </article>
  );
});

HitCompactV2.displayName = 'HitCompactV2';
